import { useEffect, useState, useRef } from 'react';
import { Modal } from '../../components/Modal';

interface UploadArtworkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (data: FormData) => Promise<void>;
  roomCode: string;
  participantId: string;
  participantName: string;
  artistProfileId?: string;
  modelName?: string;
  modelProfileId?: string;
}

export function UploadArtworkModal({
  isOpen,
  onClose,
  onUpload,
  roomCode,
  participantId,
  participantName,
  artistProfileId,
  modelName = '',
  modelProfileId,
}: UploadArtworkModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [customModelName, setCustomModelName] = useState(modelName);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCustomModelName(modelName);
  }, [modelName, isOpen]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image file must be smaller than 10MB');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select an image');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const initialModelName = modelName.trim();
      const submittedModelName = customModelName.trim();
      const modelNameWasChanged = submittedModelName !== initialModelName;
      const formData = new FormData();
      formData.append('image', selectedFile);
      if (submittedModelName) {
        formData.append('modelName', submittedModelName);
      }
      // Use artistProfileId as the participantId (profile ID is used to verify session participation)
      formData.append('participantId', artistProfileId || participantId);
      formData.append('participantName', participantName);
      if (artistProfileId) {
        formData.append('artistProfileId', artistProfileId);
      }
      if (modelProfileId && submittedModelName && !modelNameWasChanged) {
        formData.append('modelProfileId', modelProfileId);
      }

      await onUpload(formData);
      handleClose();
    } catch (err) {
      setError('Failed to upload artwork. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      onClose={handleClose}
      backdropClassName="bg-black/50 backdrop-blur-sm"
      panelClassName="bg-panel border border-line rounded-xl w-full max-w-md shadow-xl"
      closeOnBackdrop={false}
      closeOnEscape={false}
      ariaLabel="Share your work"
    >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-line-soft">
          <h2 className="font-display text-lg font-medium text-text">Share your work</h2>
          <button
            onClick={handleClose}
            className="p-1 text-text-dim hover:text-text"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <path d="M3 3l10 10M13 3L3 13"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Image upload area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            className={`relative border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              preview
                ? 'border-transparent'
                : 'border-line hover:border-text-dim'
            }`}
          >
            {preview ? (
              <div className="relative aspect-[4/3] rounded-lg overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-full object-contain bg-tile" />
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setPreview(null);
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full text-white hover:bg-black/80"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M2 2l8 8M10 2L2 10"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 px-4">
                <svg className="w-10 h-10 text-text-faint mb-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="3" y="4" width="18" height="16" rx="2"/>
                  <path d="M3 15l5-4 4 3 5-5 4 3"/>
                  <circle cx="8" cy="9" r="1.5"/>
                </svg>
                <span className="text-sm text-text-dim">Click to select an image</span>
                <span className="text-xs text-text-faint mt-1">JPEG, PNG, WebP or GIF · Max 10MB</span>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Model name input */}
          <div>
            <label className="block text-[10px] text-text-dim font-mono uppercase mb-2">
              Model
            </label>
            <input
              type="text"
              value={customModelName}
              onChange={(e) => setCustomModelName(e.target.value)}
              placeholder="Model's name (optional)"
              className="w-full h-10 px-3 bg-bg border border-line rounded-lg text-sm text-text placeholder-text-faint outline-none focus:border-text/30 transition-colors"
            />
          </div>

          {error && (
            <p className="text-sm text-warn">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 h-10 bg-panel border border-line rounded-lg text-sm font-medium text-text hover:bg-panel2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedFile || isUploading}
              className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? 'Uploading...' : 'Share'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
